import {
  Actions,
  CLEAN_UP_CURRENT_BENCHMARK_REPORT,
  DISMISS_BENCHMARK_ISSUES_SUCCESS,
  EDIT_BENCHMARK_REPORT_SUCCEEDED,
  GET_BENCHMARK_PROJECTS,
  GET_BENCHMARK_PROJECTS_FAILED,
  GET_BENCHMARK_PROJECTS_SUCCEEDED,
  GET_BENCHMARK_PROJECTS_USF_RANGE_SUCCEEDED,
  GET_BENCHMARK_REPORT,
  GET_BENCHMARK_REPORT_EDITORS_SUCCEEDED,
  GET_BENCHMARK_REPORT_FOR_EXPORTABLE,
  GET_BENCHMARK_REPORT_FOR_EXPORTABLE_SUCCEEDED,
  GET_BENCHMARK_REPORT_PREVIEW,
  GET_BENCHMARK_REPORT_PREVIEW_FAILED,
  GET_BENCHMARK_REPORT_PREVIEW_SUCCEEDED,
  GET_BENCHMARK_REPORT_SUCCEEDED,
  GET_BENCHMARK_REPORTS,
  GET_BENCHMARK_REPORTS_FAILED,
  GET_BENCHMARK_REPORTS_SUCCEEDED,
  GET_CLIENTS_OF_BENCHMARK_PROJECTS_SUCCEEDED,
  GET_LOCATIONS_FOR_BENCHMARK_REPORTS,
  GET_LOCATIONS_FOR_BENCHMARK_REPORTS_SUCCEEDED,
  GET_MISSING_DATA_SUCCEEDED,
  GET_MISSING_PROJECT_DATA_SUCCEEDED,
  HIGHLIGHT_REPORT_ID,
  REFRESH_SELECTED_BENCHMARK_PROJECTS_SUCCEEDED,
  SAVE_BENCHMARK_REPORT_CHANGES_SUCCEEDED,
  SET_BENCHMARK_REPORT_CATEGORIES_MAP,
  SET_BENCHMARK_REPORT_ICONS_MAP,
  SET_BENCHMARK_REPORTS,
  SET_SELECTED_BENCHMARK_PROJECTS,
  UPDATE_BENCHMARK_REPORT_CATEGORIES_SUCCEEDED,
  UPDATE_BENCHMARK_REPORT_ICONS_SUCCESS,
} from './actions';
import {
  DEFAULT_ICONS_MAP,
  parseIconsListToMap,
} from '../../../benchmark-reports/detail/edit-report-icons-popover/benchmark-report-icons';

import { BenchmarkReportsState } from './types';
import {
  DEFAULT_CATEGORIES_MAP,
  parseCategoriesListToMap,
} from '../../../benchmark-reports/detail/benchmark-report-categories';

const initialState: BenchmarkReportsState = {
  projects: [],
  isLoadingProjects: false,
  selectedProjects: [],
  projectsLocations: [],
  summaryIsLoading: true,
  clients: [],
  reportsList: {
    queriedAtLeastOnce: false,
    items: [],
    loading: true,
    total: 0,
  },
  editors: [],
  highlightedReportId: null,
  currentReport: null,
  currentReportCategoriesMap: DEFAULT_CATEGORIES_MAP,
  currentReportIconsMap: DEFAULT_ICONS_MAP,
  currentReportIsLoading: false,
  missingProjectData: null,
};

const reducer = (
  state: BenchmarkReportsState = initialState,
  action: Actions
): BenchmarkReportsState => {
  switch (action.type) {
    case GET_BENCHMARK_PROJECTS:
      return {
        ...state,
        isLoadingProjects: true,
      };
    case GET_BENCHMARK_PROJECTS_SUCCEEDED:
      return {
        ...state,
        isLoadingProjects: false,
        projects: action.payload,
      };
    case GET_BENCHMARK_PROJECTS_FAILED:
      return { ...state, isLoadingProjects: false };

    case GET_LOCATIONS_FOR_BENCHMARK_REPORTS:
      return {
        ...state,
        projectsLocations: [],
      };
    case GET_LOCATIONS_FOR_BENCHMARK_REPORTS_SUCCEEDED:
      return {
        ...state,
        projectsLocations: action.payload,
      };
    case GET_BENCHMARK_PROJECTS_USF_RANGE_SUCCEEDED:
      return {
        ...state,
        projectsUSFRange: action.payload,
      };
    case REFRESH_SELECTED_BENCHMARK_PROJECTS_SUCCEEDED:
    case SET_SELECTED_BENCHMARK_PROJECTS:
      return { ...state, selectedProjects: action.payload };
    case GET_BENCHMARK_REPORT_PREVIEW:
      return {
        ...state,
        summaryIsLoading: true,
      };
    case GET_BENCHMARK_REPORT_PREVIEW_SUCCEEDED:
      return {
        ...state,
        summary: action.payload,
        summaryIsLoading: false,
      };
    case GET_BENCHMARK_REPORT_PREVIEW_FAILED:
      return { ...state, summaryIsLoading: false };

    case GET_CLIENTS_OF_BENCHMARK_PROJECTS_SUCCEEDED:
      return {
        ...state,
        clients: action.payload,
      };
    case CLEAN_UP_CURRENT_BENCHMARK_REPORT:
      return {
        ...state,
        currentReport: null,
        currentReportIsLoading: false,
        selectedProjects: [],
        currentReportCategoriesMap: DEFAULT_CATEGORIES_MAP,
        currentReportIconsMap: DEFAULT_ICONS_MAP,
      };
    case SAVE_BENCHMARK_REPORT_CHANGES_SUCCEEDED:
      return {
        ...state,
        currentReport: {
          ...state.currentReport,
          benchmarkProjects: action.payload,
        },
      };
    case GET_BENCHMARK_REPORT:
      return {
        ...state,
        currentReportIsLoading: true,
      };
    case GET_BENCHMARK_REPORT_SUCCEEDED: {
      const currentReport = action.payload;

      return {
        ...state,
        currentReport,
        currentReportCategoriesMap: currentReport?.categories
          ? parseCategoriesListToMap(currentReport.categories)
          : state.currentReportCategoriesMap,
        currentReportIconsMap: currentReport?.icons?.length
          ? parseIconsListToMap(currentReport.icons)
          : state.currentReportIconsMap,
        currentReportIsLoading: false,
      };
    }
    case GET_BENCHMARK_REPORTS:
      return {
        ...state,
        reportsList: {
          ...state.reportsList,
          loading: true,
          items:
            action.payload.page === 1 || action.payload.page === undefined
              ? []
              : state.reportsList.items,
        },
      };
    case GET_BENCHMARK_REPORT_FOR_EXPORTABLE:
      return {
        ...state,
        currentReportIsLoading: true,
        summaryIsLoading: true,
      };
    case GET_BENCHMARK_REPORT_FOR_EXPORTABLE_SUCCEEDED:
      return {
        ...state,
        currentReportIsLoading: false,
        currentReport: action.payload.report,
        selectedProjects: action.payload.selectedProjects,
        summary: action.payload.reportSummary,
        summaryIsLoading: false,
      };

    case GET_BENCHMARK_REPORTS_SUCCEEDED:
      return {
        ...state,
        reportsList: {
          ...state.reportsList,
          loading: false,
          items: [...state.reportsList.items, ...action.payload.reports],
          total: action.payload.total,
        },
      };
    case GET_BENCHMARK_REPORTS_FAILED:
      return {
        ...state,
        reportsList: {
          ...state.reportsList,
          loading: false,
          items: [],
        },
      };

    case EDIT_BENCHMARK_REPORT_SUCCEEDED:
      return {
        ...state,
        currentReport: { ...state.currentReport, ...action.payload },
      };

    case GET_BENCHMARK_REPORT_EDITORS_SUCCEEDED:
      return {
        ...state,
        editors: action.payload,
      };

    case SET_BENCHMARK_REPORTS:
      return {
        ...state,
        reportsList: {
          ...state.reportsList,
          items: action.payload,
        },
      };

    case DISMISS_BENCHMARK_ISSUES_SUCCESS:
    case GET_MISSING_PROJECT_DATA_SUCCEEDED:
      return {
        ...state,
        missingProjectData: action.payload,
      };
    case GET_MISSING_DATA_SUCCEEDED:
      return {
        ...state,
        missingData: action.payload,
      };
    case HIGHLIGHT_REPORT_ID:
      return {
        ...state,
        highlightedReportId: action.payload,
      };

    case SET_BENCHMARK_REPORT_CATEGORIES_MAP:
      return {
        ...state,
        currentReportCategoriesMap: action.payload,
      };

    case SET_BENCHMARK_REPORT_ICONS_MAP:
      return {
        ...state,
        currentReportIconsMap: action.payload,
      };

    case UPDATE_BENCHMARK_REPORT_CATEGORIES_SUCCEEDED:
      return {
        ...state,
        currentReport: {
          ...state.currentReport,
          categories: action.payload,
        },
        currentReportCategoriesMap: parseCategoriesListToMap(action.payload),
      };
    case UPDATE_BENCHMARK_REPORT_ICONS_SUCCESS:
      return {
        ...state,
        currentReport: {
          ...state.currentReport,
          icons: action.payload,
        },
      };
    default:
      return state;
  }
};

export default reducer;
